import { useCallback, useRef, useState } from 'react';
import {
  changePlan,
  compedRefusal,
  previewPlanChange,
  PlanChangePreview,
  PlanChangeResult,
} from '../../api/plan-client';
import { startUnitCardSetup } from '../../api/units-client';
import { money, paymentLinkOf, perSuffix } from './use-unit-purchase';
import { formatDate } from '../../function-utils/locale';
import type {
  StripeCardConfirmation,
  StripeIntent,
  StripeSummaryLine,
} from './stripe-card-modal';

/* ───────────────────────────────────────────────────────────────────────────
 * CHANGING PLAN — the same four steps as buying units (WP-23)
 *
 * `dashboard-billing.tsx`'s plan cards used to POST /subscriptions/checkout and
 * then `window.location.href = data.url`: a full-page redirect out of the app,
 * with no quote, no confirmation and no way back except Stripe's own cancel
 * link. Worse, hosted Checkout CREATES a subscription and nothing cancelled the
 * old one — an already-invoiced customer was billed twice for the same month.
 *
 * The change now runs through the SAME modal the unit purchase uses, in the
 * same order, for the same reasons:
 *
 *   1. QUOTE     ask the server what this costs. Every figure comes off that
 *                response; nothing here multiplies a rate by a count (D-12).
 *   2. CONFIRM   show the figures as SEPARATE labelled lines. A trialing org is
 *                charged $0.00 today while its monthly bill changes — one
 *                number cannot say both things.
 *   3. CARD      only for an UPGRADE by an org with no card, and only when the
 *                server also says what adding one would do. Refusing a
 *                DOWNGRADE for want of a card would trap an org in the more
 *                expensive plan.
 *   4. COMMIT    POST the plan id. The server updates the EXISTING subscription
 *                at Stripe and mirrors two columns locally, in that order.
 *
 * WHY IT IS A SEPARATE HOOK FROM useUnitPurchase: the two share a modal and a
 * money-formatting rule, and nothing else. A unit purchase changes the QUANTITY
 * on a subscription; a plan change changes its PRICE. Folding them together
 * would mean one code path deciding which Stripe field to write from a flag,
 * which is exactly how the wrong field gets written.
 * ─────────────────────────────────────────────────────────────────────────── */

export interface UsePlanChange {
  /** The server's quote, or null before one has arrived. */
  preview: PlanChangePreview | null;
  /** True while the quote is in flight. */
  quoting: boolean;
  /** The plan this change is for. */
  planId: number | null;
  /** The server's own refusal, or why the quote failed. '' when nothing is wrong. */
  refusal: string;
  /**
   * True only when the server asked for a card AND told us what adding one
   * would do. A refusal without `recovery` is not recoverable from this screen,
   * so the card form stays shut.
   */
  collectCard: boolean;
  /** True when the change cannot proceed at all. */
  blocked: boolean;
  /** Stripe's page for a payment that did not go through, or null. */
  paymentLink: string | null;
  summaryRows: StripeSummaryLine[];
  quote: (planId: number) => Promise<void>;
  reset: () => void;
  /** Passed to the card modal. Runs at most once per modal, by contract. */
  prepareIntent: () => Promise<StripeIntent>;
  /** The card path's callback: commits using the confirmation as proof. */
  confirmed: (result: StripeCardConfirmation) => Promise<PlanChangeResult>;
  /** The no-card path's callback: commits with no proof. */
  submit: () => Promise<PlanChangeResult>;
}

// A complimentary account's refusal is named for what it is before anything
// else is read (COMPED_ACCOUNT, api/plan-client.ts).
const serverMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { message?: string } } };

  return (
    compedRefusal(err) ||
    (e && e.response && e.response.data && e.response.data.message) ||
    fallback
  );
};

export const usePlanChange = (): UsePlanChange => {
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [planId, setPlanId] = useState<number | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [commitPaymentLink, setCommitPaymentLink] = useState<string | null>(
    null
  );
  // The id the server minted, kept as a fallback for the id Stripe reports back
  // so a confirmation that resolves without one still carries a provable proof.
  const setupIntentIdRef = useRef<string | null>(null);
  // Read inside async callbacks the modal owns; a ref keeps them from closing
  // over a stale render.
  const planIdRef = useRef<number | null>(null);
  // The quote the user confirmed, read by the commit for its `proration_date`
  // so the change is prorated from the instant the figures were shown.
  const previewRef = useRef<PlanChangePreview | null>(null);

  const reset = useCallback(() => {
    previewRef.current = null;
    setPreview(null);
    setQuoteError('');
    setCommitPaymentLink(null);
    setPlanId(null);
    planIdRef.current = null;
    setupIntentIdRef.current = null;
  }, []);

  const quote = useCallback(async (next: number) => {
    previewRef.current = null;
    setPreview(null);
    setQuoteError('');
    setCommitPaymentLink(null);
    setPlanId(next);
    planIdRef.current = next;
    setupIntentIdRef.current = null;
    setQuoting(true);
    try {
      const fresh = await previewPlanChange(next);
      previewRef.current = fresh;
      setPreview(fresh);
    } catch (err) {
      // No quote means no confirmation. Say so rather than offering a button
      // that would commit a change nobody was shown the price of.
      setQuoteError(
        serverMessage(err, 'Could not price this plan change. Please try again.')
      );
    } finally {
      setQuoting(false);
    }
  }, []);

  // The SAME card route the unit purchase uses. There is one "add a card"
  // surface for an existing organisation (POST /subscriptions/payment-method)
  // and one proof format; a second one would be a second thing to keep
  // verified, and the server binds the proof to the org either way.
  const prepareIntent = useCallback(async (): Promise<StripeIntent> => {
    const setup = await startUnitCardSetup();
    setupIntentIdRef.current = setup.setup_intent_id || null;

    // 'setup' is not decoration: the modal calls confirmCardSetup or
    // confirmCardPayment on the strength of it, and a SetupIntent confirmed as
    // a payment would report money moving when none did.
    return { mode: 'setup', clientSecret: setup.client_secret };
  }, []);

  const commit = useCallback(
    async (proof: string | null): Promise<PlanChangeResult> => {
      const id = planIdRef.current;
      if (id == null) {
        throw new Error('No plan was selected.');
      }

      try {
        const shown = previewRef.current;

        return await changePlan(id, proof, shown ? shown.proration_date : null);
      } catch (err) {
        setCommitPaymentLink(paymentLinkOf(err));
        throw err;
      }
    },
    []
  );

  const confirmed = useCallback(
    async (result: StripeCardConfirmation) => {
      // A PaymentIntent here would mean money moved on a flow that is supposed
      // to SAVE a card and let the subscription bill it. The server would refuse
      // the proof anyway; failing loudly here says which side is wrong.
      if (result.confirmed !== 'confirmCardSetup') {
        throw new Error(
          'Unexpected payment confirmation while adding a card. Your plan was ' +
            'not changed — please contact support.'
        );
      }

      return commit(result.intentId || setupIntentIdRef.current);
    },
    [commit]
  );

  const submit = useCallback(() => commit(null), [commit]);

  const recovery = (preview && preview.recovery) || null;
  const refusing = !!preview && preview.mode === 'refuse';
  const collectCard = !!(
    refusing &&
    preview &&
    preview.requires_card &&
    recovery &&
    recovery.available
  );
  const refusal =
    quoteError ||
    (refusing
      ? (preview && preview.message) || 'This plan change cannot be made right now.'
      : '');
  const blocked = !preview || (refusing && !collectCard);

  const intervalWord = (iv?: string | null) =>
    iv === 'year' ? 'Annual' : 'Monthly';
  const dateOf = (isoDate?: string | null) =>
    isoDate ? formatDate(isoDate) || '—' : '—';

  const summaryRows: StripeSummaryLine[] = [];
  if (preview && preview.plan_name) {
    const chargeNow = preview.mode === 'charge_now';
    summaryRows.push({
      label: 'Plan',
      value: `${preview.current_plan_name || 'Current plan'} → ${preview.plan_name}`,
    });
    // An interval change says so in words: which way, and — for a scheduled
    // annual -> monthly — from when.
    if (
      preview.interval_now &&
      preview.interval_after &&
      preview.interval_now !== preview.interval_after
    ) {
      summaryRows.push({
        label: 'Billing',
        value: `${intervalWord(preview.interval_now)} → ${intervalWord(
          preview.interval_after
        )}`,
      });
    }
    if (preview.mode === 'scheduled') {
      summaryRows.push({
        label: 'Takes effect',
        value: dateOf(preview.effective_at),
      });
    }
    summaryRows.push({
      label: 'Units',
      value: `${preview.quantity}`,
    });
    summaryRows.push({
      label: 'Rate',
      value: `${money(preview.rate_cents, preview.currency)}/unit/${preview.interval}`,
    });
    summaryRows.push({
      label: chargeNow ? 'Charged today' : 'Due today',
      value:
        collectCard && recovery
          ? money(recovery.first_charge_cents, preview.currency)
          : preview.amount_due_now_cents == null
          ? // "we could not work it out" and "you will not be charged today"
            // must not look the same.
            'Not available'
          : money(preview.amount_due_now_cents, preview.currency),
    });
    // Earlier changes' adjustments are NOT in today's charge — Stripe settles
    // them on the next regular invoice — so they are named apart.
    if (chargeNow && preview.pending_adjustments_cents) {
      summaryRows.push({
        label: 'Earlier adjustments, next invoice',
        // A credit reads as a credit — `money` printed "$-45.00".
        value:
          preview.pending_adjustments_cents < 0
            ? `${money(-preview.pending_adjustments_cents, preview.currency)} credit`
            : money(preview.pending_adjustments_cents, preview.currency),
      });
    }
    // On a charge-now change the proration IS today's charge.
    if (
      !chargeNow &&
      preview.proration_cents != null &&
      preview.proration_cents !== 0
    ) {
      summaryRows.push({
        label: 'On your next invoice',
        value: money(preview.proration_cents, preview.currency),
      });
    }
    summaryRows.push({
      label: `New total per ${preview.interval}`,
      value: `${money(
        preview.new_recurring_total_cents,
        preview.currency
      )}/${perSuffix(preview.interval)}`,
      total: true,
    });
  }

  return {
    preview,
    quoting,
    planId,
    refusal,
    collectCard,
    blocked,
    paymentLink:
      commitPaymentLink ||
      (preview && preview.payment && preview.payment.hosted_invoice_url) ||
      null,
    summaryRows,
    quote,
    reset,
    prepareIntent,
    confirmed,
    submit,
  };
};
